import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Routes as SalesRoutes } from "ui/modules/sales/navigation/routes";
import { transformRoute, useTranslate } from "helpers";

import { getInquiriesAPI } from "services/api/sales/inquiries/api";
import { InquiryRow } from "services/api/sales/inquiries/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

import { InquirySearchPageCmp } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";

import { SALES_INQUIRY_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

const InquirySearchPage: InquirySearchPageCmp = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<DataWithPagination<InquiryRow>>(initialDataWithPagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    search?: string | null,
    inquirySourceId?: string | null,
    status?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getInquiriesAPI(pagination, search, inquirySourceId, status);
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleRowClick = (id: string) => {
    navigate(transformRoute(SalesRoutes.InquiryDetailPageRouter, { id }));
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const columns: Column[] = [
    {
      title: "admin.sales.inquiry.search.customer",
      key: "customer",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InquiryRow) => (
        <div>
          <div className="text-md font-medium">{row.customerName ?? "-"}</div>
          <div className="text-xs text-gray-500">{row.customerEmail}</div>
        </div>
      ),
    },
    {
      title: "admin.sales.inquiry.search.source",
      key: "source",
      field: "inquirySourceName",
      type: ColumnTypes.string,
    },
    {
      title: "admin.sales.inquiry.search.status",
      key: "status",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InquiryRow) => (
        <span className="rounded-sm uppercase text-xs font-normal inline-block text-center min-w-[80px] border px-2.5 py-1">
          {row.status}
        </span>
      ),
    },
    {
      title: "admin.sales.inquiry.search.assigned",
      key: "assigned",
      field: "assignedUserName",
      type: ColumnTypes.string,
    },
    {
      title: "admin.sales.inquiry.search.created",
      key: "created",
      field: "",
      type: ColumnTypes.custom,
      render: (row: InquiryRow) => (
        <div className="text-sm">
          {row.created ? new Date(row.created).toLocaleDateString("hr-HR") : "-"}
        </div>
      ),
    },
  ];

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={SALES_INQUIRY_SEARCH_PAGE_TITLE.text}
                  icon={SALES_INQUIRY_SEARCH_PAGE_TITLE.icon}
                  iconColor={SALES_INQUIRY_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.sales.inquiry.search.noresults")} />
          ) : (
            <TableSearch
              columns={columns}
              data={searchResults.data}
              pagination={searchResults.pagination || initialDataWithPagination.pagination}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onRowClick={handleRowClick}
              isMinimized={true}
              size="full"
            />
          )}
        </div>
      </div>
    </>
  );
};

InquirySearchPage.route = SalesRoutes.InquirySearchPageRouter;

export default InquirySearchPage;
